import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Link2,
  Plus,
  Search,
  Copy,
  Check,
  Edit2,
  Trash2,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  ArrowRight,
  LayoutTemplate,
  Image as ImageIcon,
  Star,
  StarOff,
  BarChart3,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import api from '../../services/api';
import ShortLinkAnalyticsModal from './ShortLinkAnalyticsModal';
import {
  listShortLinks,
  createShortLink,
  updateShortLink,
  deleteShortLink,
  buildShortUrl,
  ShortLink,
  LinkType,
  InterstitialButton,
  InterstitialConfig,
} from '../../services/shortLinkService';

const PAGE_SIZE = 20;

const ShortLinksPage: React.FC = () => {
  const { t } = useTranslation();

  const [links, setLinks] = useState<ShortLink[]>([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  // Modal state. O form acumula tudo: campos comuns + interstitial.
  // Quando link_type=direct, os campos de interstitial ficam ignorados no submit.
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<ShortLink | null>(null);
  const [form, setForm] = useState<{
    title: string;
    target_url: string;
    code: string;
    link_type: LinkType;
    interstitial_title: string;
    interstitial_description: string;
    interstitial_logo_url: string;
    interstitial_buttons: InterstitialButton[];
  }>({
    title: '',
    target_url: '',
    code: '',
    link_type: 'direct',
    interstitial_title: '',
    interstitial_description: '',
    interstitial_logo_url: '',
    interstitial_buttons: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete confirmation
  const [confirmDelete, setConfirmDelete] = useState<ShortLink | null>(null);

  // Analytics modal — armazena qual link tá sendo visualizado
  const [analyticsLink, setAnalyticsLink] = useState<ShortLink | null>(null);

  // Copy feedback (ids that just got copied)
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Logo upload state — loading flag + hidden file input ref
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await listShortLinks({ search: search || undefined, page, limit: PAGE_SIZE });
      setLinks(res.data);
      setPagination({
        total: res.pagination.total,
        page: res.pagination.page,
        totalPages: Math.max(1, res.pagination.totalPages),
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('shortLinks.loadError', 'Erro ao carregar links'));
    } finally {
      setIsLoading(false);
    }
  }, [search, page, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Debounce do search input
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      title: '',
      target_url: '',
      code: '',
      link_type: 'direct',
      interstitial_title: '',
      interstitial_description: '',
      interstitial_logo_url: '',
      interstitial_buttons: [],
    });
    setIsModalOpen(true);
  };

  const openEdit = (link: ShortLink) => {
    setEditing(link);
    setForm({
      title: link.title || '',
      target_url: link.target_url,
      code: link.code,
      link_type: link.link_type || 'direct',
      interstitial_title: link.interstitial_config?.title || '',
      interstitial_description: link.interstitial_config?.description || '',
      interstitial_logo_url: link.interstitial_config?.logo_url || '',
      interstitial_buttons: link.interstitial_config?.buttons
        ? link.interstitial_config.buttons.map(b => ({ ...b }))
        : [],
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSubmitting) return;
    setIsModalOpen(false);
    setEditing(null);
  };

  const submit = async () => {
    let url = form.target_url.trim();
    if (form.link_type === 'interstitial') {
      const valid = form.interstitial_buttons.filter(b => b.label.trim() && b.url.trim());
      if (valid.length === 0) {
        toast.error(t('shortLinks.buttonRequired', 'Adicione pelo menos um botão com label e URL.'));
        return;
      }
      // Na interstitial o target_url no DB é fallback — usa o do botão primário (ou primeiro)
      if (!url) {
        const primary = valid.find(b => b.primary) || valid[0];
        url = primary.url.trim();
      }
    } else if (!url) {
      toast.error(t('shortLinks.urlRequired', 'A URL de destino é obrigatória.'));
      return;
    }
    setIsSubmitting(true);
    try {
      const interstitial_config: InterstitialConfig | null = form.link_type === 'interstitial'
        ? {
            title: form.interstitial_title.trim() || null,
            description: form.interstitial_description.trim() || null,
            logo_url: form.interstitial_logo_url.trim() || null,
            buttons: form.interstitial_buttons
              .filter(b => b.label.trim() && b.url.trim())
              .map(b => ({ label: b.label.trim(), url: b.url.trim(), primary: !!b.primary })),
          }
        : null;
      if (editing) {
        await updateShortLink(editing.id, {
          target_url: url,
          title: form.title.trim() || undefined,
          code: form.code.trim() && form.code.trim() !== editing.code ? form.code.trim() : undefined,
          link_type: form.link_type,
          interstitial_config,
        });
        toast.success(t('shortLinks.updated', 'Link atualizado'));
      } else {
        await createShortLink({
          target_url: url,
          title: form.title.trim() || undefined,
          code: form.code.trim() || undefined,
          link_type: form.link_type,
          interstitial_config: interstitial_config || undefined,
        });
        toast.success(t('shortLinks.created', 'Link criado'));
      }
      setIsModalOpen(false);
      setEditing(null);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('shortLinks.saveError', 'Erro ao salvar link'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleStatus = async (link: ShortLink) => {
    const nextStatus = link.status === 'active' ? 'paused' : 'active';
    try {
      await updateShortLink(link.id, { status: nextStatus });
      toast.success(
        nextStatus === 'active'
          ? t('shortLinks.activated', 'Link ativado')
          : t('shortLinks.paused', 'Link pausado')
      );
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('shortLinks.saveError', 'Erro ao salvar link'));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteShortLink(confirmDelete.id);
      toast.success(t('shortLinks.deleted', 'Link removido'));
      setConfirmDelete(null);
      // Recarrega; se a página esvaziar, volta uma página
      if (links.length === 1 && page > 1) setPage(page - 1);
      else await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('shortLinks.deleteError', 'Erro ao remover link'));
    }
  };

  // Faz upload da imagem do logo pro S3 via endpoint /media/upload e popula
  // o campo interstitial_logo_url com a URL retornada.
  const handleLogoFile = async (file: File | null) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('shortLinks.logoTooLarge', 'Logo muito grande. Máximo 5MB.'));
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error(t('shortLinks.logoNotImage', 'Selecione um arquivo de imagem.'));
      return;
    }
    setIsUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/media/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.url;
      if (!url) throw new Error('Upload sem URL');
      setForm(f => ({ ...f, interstitial_logo_url: url }));
      toast.success(t('shortLinks.logoUploaded', 'Logo enviado.'));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('shortLinks.logoUploadError', 'Erro ao enviar logo'));
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const addButton = () => {
    if (form.interstitial_buttons.length >= 6) return;
    setForm(f => ({
      ...f,
      interstitial_buttons: [
        ...f.interstitial_buttons,
        { label: '', url: '', primary: f.interstitial_buttons.length === 0 },
      ],
    }));
  };
  const updateButton = (idx: number, patch: Partial<InterstitialButton>) => {
    setForm(f => ({
      ...f,
      interstitial_buttons: f.interstitial_buttons.map((b, i) => i === idx ? { ...b, ...patch } : b),
    }));
  };
  const removeButton = (idx: number) => {
    setForm(f => ({
      ...f,
      interstitial_buttons: f.interstitial_buttons.filter((_, i) => i !== idx),
    }));
  };
  const setPrimaryButton = (idx: number) => {
    setForm(f => ({
      ...f,
      interstitial_buttons: f.interstitial_buttons.map((b, i) => ({ ...b, primary: i === idx })),
    }));
  };

  const copy = async (link: ShortLink) => {
    const url = buildShortUrl(link.code);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(link.id);
      setTimeout(() => setCopiedId((cur) => (cur === link.id ? null : cur)), 1500);
    } catch {
      toast.error(t('shortLinks.copyError', 'Não consegui copiar — copie manualmente'));
    }
  };

  // Botões de cada link (mesmos na tabela do computador e nos cartões do celular)
  const renderActions = (link: ShortLink) => (
    <div className="flex items-center justify-end gap-1">
      <button
        type="button"
        onClick={() => copy(link)}
        title={t('shortLinks.copyTooltip', 'Copiar link') as string}
        className="p-1.5 rounded-md text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
      >
        {copiedId === link.id ? (
          <Check className="w-4 h-4 text-emerald-500" />
        ) : (
          <Copy className="w-4 h-4" />
        )}
      </button>
      <button
        type="button"
        onClick={() => setAnalyticsLink(link)}
        title={t('shortLinks.analytics.open', 'Ver analytics') as string}
        className="p-1.5 rounded-md text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
      >
        <BarChart3 className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => toggleStatus(link)}
        title={
          (link.status === 'active'
            ? t('shortLinks.pause', 'Pausar')
            : t('shortLinks.activate', 'Ativar')) as string
        }
        className="p-1.5 rounded-md text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
      >
        {link.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>
      <button
        type="button"
        onClick={() => openEdit(link)}
        title={t('common.edit', 'Editar') as string}
        className="p-1.5 rounded-md text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
      >
        <Edit2 className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => setConfirmDelete(link)}
        title={t('common.delete', 'Remover') as string}
        className="p-1.5 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      {/* Header (sem "voltar para campanhas": a tela agora é uma aba de Site e blog) */}
      <PageHeader
        icon={Link2}
        title={t('shortLinks.title', 'Links curtos')}
        description={t('shortLinks.subtitle', 'Links fáceis de divulgar, que contam quantas pessoas clicaram')}
        actions={
          <Button onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />} className="flex-1 sm:flex-none">
            {t('shortLinks.newLink', 'Novo link')}
          </Button>
        }
      />

      {/* Search */}
      <Card padding={false}>
        <CardContent className="p-3">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('shortLinks.searchPlaceholder', 'Buscar por título, URL ou código') as string}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">{t('common.loading', 'Carregando...')}</div>
          ) : links.length === 0 ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">
              <Link2 className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p className="font-medium text-gray-900 dark:text-white">{t('shortLinks.empty', 'Nenhum link ainda')}</p>
              <p className="text-sm mt-1">{t('shortLinks.emptyHint', 'Crie um link curto para colocar na bio, no WhatsApp ou num anúncio.')}</p>
              <Button onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />} className="mt-4">
                {t('shortLinks.createFirst', 'Criar meu primeiro link')}
              </Button>
            </div>
          ) : (
            <>
            {/* Celular: um cartão por link */}
            <ul className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
              {links.map((link) => {
                const shortUrl = buildShortUrl(link.code);
                return (
                  <li key={link.id} className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        {link.title && <p className="font-medium text-gray-900 dark:text-white truncate">{link.title}</p>}
                        <code className="block text-xs font-mono text-primary-600 dark:text-primary-400 truncate">{shortUrl}</code>
                      </div>
                      <Badge variant={link.status === 'active' ? 'success' : link.status === 'paused' ? 'warning' : 'gray'}>
                        {t(`shortLinks.status.${link.status}`, link.status)}
                      </Badge>
                    </div>
                    <a href={link.target_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 min-w-0">
                      <span className="truncate">{link.target_url}</span>
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        <strong>{link.click_count.toLocaleString()}</strong> {t('shortLinks.clicksLabel', 'cliques')}
                      </span>
                      {renderActions(link)}
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3 text-left">{t('shortLinks.colShort', 'Link Curto')}</th>
                    <th className="px-4 py-3 text-left">{t('shortLinks.colTarget', 'URL de Destino')}</th>
                    <th className="px-4 py-3 text-center">{t('shortLinks.colClicks', 'Cliques')}</th>
                    <th className="px-4 py-3 text-left">{t('shortLinks.colStatus', 'Status')}</th>
                    <th className="px-4 py-3 text-right">{t('shortLinks.colActions', 'Ações')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {links.map((link) => {
                    const shortUrl = buildShortUrl(link.code);
                    return (
                      <tr key={link.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                        <td className="px-4 py-3 max-w-xs">
                          <div className="flex items-center gap-2">
                            {link.title && (
                              <div className="font-medium text-gray-900 dark:text-white truncate" title={link.title}>
                                {link.title}
                              </div>
                            )}
                            {link.link_type === 'interstitial' && (
                              <span
                                className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                                title={t('shortLinks.typeInterstitial', 'Página intermediária') as string}
                              >
                                <LayoutTemplate className="w-3 h-3" />
                                {t('shortLinks.typeInterstitialBadge', 'Página')}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-primary-600 dark:text-primary-400">
                            <code className="text-xs font-mono truncate max-w-[200px]" title={shortUrl}>{shortUrl}</code>
                          </div>
                        </td>
                        <td className="px-4 py-3 max-w-md">
                          <a
                            href={link.target_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 truncate max-w-[400px]"
                            title={link.target_url}
                          >
                            <span className="truncate">{link.target_url}</span>
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          </a>
                        </td>
                        <td className="px-4 py-3 text-center font-medium text-gray-900 dark:text-white">
                          {link.click_count.toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={link.status === 'active' ? 'success' : link.status === 'paused' ? 'warning' : 'gray'}
                          >
                            {t(`shortLinks.status.${link.status}`, link.status)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {renderActions(link)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}

          {/* Pagination */}
          {!isLoading && links.length > 0 && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
              <span>
                {t('shortLinks.pageInfo', 'Página {{page}} de {{total}} · {{count}} links')
                  .replace('{{page}}', String(pagination.page))
                  .replace('{{total}}', String(pagination.totalPages))
                  .replace('{{count}}', String(pagination.total))}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                  className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label={t('common.previous', 'Anterior') as string}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label={t('common.next', 'Próximo') as string}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editing ? t('shortLinks.editTitle', 'Editar link') as string : t('shortLinks.newTitle', 'Novo link curto') as string}
      >
        <div className="space-y-4">
          {/* Tipo: direto vs página intermediária — toggle visual com 2 cards */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
              {t('shortLinks.fieldType', 'Tipo de link')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, link_type: 'direct' }))}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  form.link_type === 'direct'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <ArrowRight className="w-4 h-4 text-primary-600" />
                  <span className="font-medium text-sm">{t('shortLinks.typeDirect', 'Direto')}</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('shortLinks.typeDirectHint', 'Redirect 302 imediato pra URL.')}
                </p>
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({
                  ...f,
                  link_type: 'interstitial',
                  // Cria 1 botão default se vazio, pré-preenchido com a target_url
                  interstitial_buttons: f.interstitial_buttons.length > 0
                    ? f.interstitial_buttons
                    : [{ label: t('shortLinks.defaultButton', 'Continuar') as string, url: f.target_url || '', primary: true }],
                }))}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  form.link_type === 'interstitial'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <LayoutTemplate className="w-4 h-4 text-primary-600" />
                  <span className="font-medium text-sm">{t('shortLinks.typeInterstitial', 'Página intermediária')}</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('shortLinks.typeInterstitialHint', 'Página com logo, texto e botões.')}
                </p>
              </button>
            </div>
          </div>

          {/* URL de destino — só importa pra direct, mas mantém pra interstitial como fallback */}
          {form.link_type === 'direct' && (
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                {t('shortLinks.fieldUrl', 'URL de destino')} <span className="text-red-500">*</span>
              </label>
              <Input
                value={form.target_url}
                onChange={(e) => setForm((f) => ({ ...f, target_url: e.target.value }))}
                placeholder="https://exemplo.com/pagina-longa?utm_source=google&utm_campaign=..."
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('shortLinks.urlPreserveHint', 'A URL é preservada integralmente, incluindo todos os parâmetros e fragmentos.')}
              </p>
            </div>
          )}

          {/* Bloco da página intermediária */}
          {form.link_type === 'interstitial' && (
            <div className="space-y-4 bg-gray-50 dark:bg-gray-800/30 rounded-lg p-4 border border-gray-100 dark:border-gray-700">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  {t('shortLinks.fieldInterstitialTitle', 'Título da página')}
                </label>
                <Input
                  value={form.interstitial_title}
                  onChange={(e) => setForm(f => ({ ...f, interstitial_title: e.target.value }))}
                  placeholder={t('shortLinks.interstitialTitlePlaceholder', 'Ex: Vamos conversar?') as string}
                  maxLength={120}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  {t('shortLinks.fieldInterstitialDesc', 'Descrição')}
                </label>
                <textarea
                  value={form.interstitial_description}
                  onChange={(e) => setForm(f => ({ ...f, interstitial_description: e.target.value }))}
                  placeholder={t('shortLinks.interstitialDescPlaceholder', 'Mensagem opcional pro visitante.') as string}
                  maxLength={500}
                  rows={3}
                  className="input w-full resize-none"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  <span className="inline-flex items-center gap-1">
                    <ImageIcon className="w-3.5 h-3.5" />
                    {t('shortLinks.fieldLogo', 'Logo (opcional)')}
                  </span>
                </label>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleLogoFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
                {form.interstitial_logo_url ? (
                  <div className="flex items-center gap-3 p-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md">
                    <img
                      src={form.interstitial_logo_url}
                      alt=""
                      className="w-12 h-12 rounded-md object-contain bg-gray-50 dark:bg-gray-800"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-700 dark:text-gray-300 truncate" title={form.interstitial_logo_url}>
                        {form.interstitial_logo_url}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={isUploadingLogo}
                      className="text-xs px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-50"
                    >
                      {isUploadingLogo
                        ? t('shortLinks.uploading', 'Enviando...')
                        : t('shortLinks.replaceLogo', 'Substituir')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, interstitial_logo_url: '' }))}
                      title={t('common.remove', 'Remover') as string}
                      className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={isUploadingLogo}
                    className="w-full p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-600 dark:text-gray-400 hover:border-primary-400 hover:bg-primary-50/30 dark:hover:bg-primary-900/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isUploadingLogo ? (
                      <span>{t('shortLinks.uploading', 'Enviando...')}</span>
                    ) : (
                      <>
                        <ImageIcon className="w-4 h-4" />
                        {t('shortLinks.uploadLogo', 'Subir imagem')}
                      </>
                    )}
                  </button>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {t('shortLinks.logoHint', 'PNG/JPG/SVG até 5MB. Recomendado: quadrado, fundo transparente.')}
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('shortLinks.fieldButtons', 'Botões')} <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={addButton}
                    disabled={form.interstitial_buttons.length >= 6}
                    className="text-xs px-2 py-1 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 inline-flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {t('shortLinks.addButton', 'Adicionar')}
                  </button>
                </div>
                {form.interstitial_buttons.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">
                    {t('shortLinks.noButtonsYet', 'Nenhum botão ainda. Clique em "Adicionar" pra criar.')}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {form.interstitial_buttons.map((btn, idx) => (
                      <div key={idx} className="flex flex-wrap gap-2 items-start bg-white dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-700 p-2">
                        <Input
                          value={btn.label}
                          onChange={(e) => updateButton(idx, { label: e.target.value })}
                          placeholder={t('shortLinks.buttonLabelPlaceholder', 'Texto') as string}
                          maxLength={60}
                          className="flex-1 min-w-[120px]"
                        />
                        <Input
                          value={btn.url}
                          onChange={(e) => updateButton(idx, { url: e.target.value })}
                          placeholder="https://wa.me/..."
                          className="flex-1 min-w-[200px]"
                        />
                        <button
                          type="button"
                          onClick={() => setPrimaryButton(idx)}
                          title={t('shortLinks.makePrimary', 'Definir como principal') as string}
                          className={`p-2 rounded-md border ${
                            btn.primary
                              ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-300 text-amber-600'
                              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 hover:text-amber-600'
                          }`}
                        >
                          {btn.primary ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeButton(idx)}
                          title={t('common.remove', 'Remover') as string}
                          className="p-2 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 border border-transparent hover:border-red-200 dark:hover:border-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  {t('shortLinks.buttonHint', 'Até 6 botões. A estrela marca o botão principal (destaque visual).')}
                </p>
              </div>
            </div>
          )}

          {/* Campos administrativos */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              {t('shortLinks.fieldTitle', 'Título interno (opcional)')}
            </label>
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder={t('shortLinks.titlePlaceholder', 'Ex: Campanha Black Friday') as string}
              maxLength={255}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('shortLinks.titleInternalHint', 'Só pra você identificar na lista. Não aparece pro visitante.')}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              {t('shortLinks.fieldCode', 'Código personalizado (opcional)')}
            </label>
            <Input
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              placeholder={t('shortLinks.codePlaceholder', 'Auto-gerado se vazio') as string}
              maxLength={32}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('shortLinks.codeHint', '3-32 caracteres: letras, números, hífen ou underline.')}
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={closeModal} disabled={isSubmitting}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button
              onClick={submit}
              disabled={
                isSubmitting ||
                (form.link_type === 'direct'
                  ? !form.target_url.trim()
                  : form.interstitial_buttons.filter(b => b.label.trim() && b.url.trim()).length === 0)
              }
            >
              {isSubmitting
                ? t('common.saving', 'Salvando...')
                : editing
                  ? t('common.save', 'Salvar')
                  : t('common.create', 'Criar')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Analytics modal */}
      <ShortLinkAnalyticsModal link={analyticsLink} onClose={() => setAnalyticsLink(null)} />

      {/* Delete confirmation */}
      <Modal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title={t('shortLinks.deleteTitle', 'Remover link?') as string}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {t('shortLinks.deleteConfirm', 'Tem certeza que deseja remover este link? Esta ação não pode ser desfeita.')}
          </p>
          {confirmDelete && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-3 text-sm font-mono break-all">
              {buildShortUrl(confirmDelete.code)}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button variant="danger" onClick={doDelete}>
              {t('common.remove', 'Remover')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ShortLinksPage;
